'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ConsultationTypeCard from './ConsultationTypeCard';
import CounselorCard from './CounselorCard';
import TimeSlotButton from './TimeSlotButton';
import PreparationChecklist from './PreparationChecklist';
import BookingSummary from './BookingSummary';
import ContactForm from './ContactForm';
import VideoTestTool from './VideoTestTool';



interface AvailableDate {
  id: string;
  date: string;
  display: string;
}

function generateFutureDates(days = 5): AvailableDate[] {
  const dates: AvailableDate[] = [];
  const today = new Date();

  // Holiday list (YYYY-MM-DD)
  const holidays = [
  '2026-01-26',
  '2026-03-04',
  '2026-08-15',
  '2026-08-28',
  '2026-10-02',
  '2026-10-20',
  '2026-11-09',
  '2026-11-10',
  '2026-11-11',
  '2026-12-25'];


  let count = 0;
  let offset = 0;

  while (count < days) {
    const d = new Date(today);
    d.setDate(today.getDate() + offset);

    const isSunday = d.getDay() === 0;
    const dateString = d.toISOString().split('T')[0];
    const isHoliday = holidays.includes(dateString);

    if (!isSunday && !isHoliday) {
      dates.push({
        id: `${count + 1}`,
        date: dateString,
        display: d.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        })
      });

      count++;
    }

    offset++;
  }

  return dates;
}


interface ConsultationType {
  id: string;
  type: string;
  duration: string;
  description: string;
  price: string;
  features: string[];
  isPopular?: boolean;
}

interface Counselor {
  id: string;
  name: string;
  title: string;
  image: string;
  alt: string;
  specializations: string[];
  languages: string[];
  experience: string;
  rating: number;
  reviewCount: number;
}

interface TimeSlot {
  id: string;
  time: string;
  isAvailable: boolean;
}

interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export default function ConsultationBookingInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedConsultationType, setSelectedConsultationType] = useState<string>('');
  const [selectedCounselor, setSelectedCounselor] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
  { id: '1', text: 'Prepare list of preferred universities and programs', completed: false },
  { id: '2', text: 'Gather academic transcripts and certificates', completed: false },
  { id: '3', text: 'List your career goals and aspirations', completed: false },
  { id: '4', text: 'Note any budget constraints or scholarship interests', completed: false },
  { id: '5', text: 'Prepare questions about visa process and requirements', completed: false }]
  );
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    preferredDestination: '',
    currentEducation: '',
    additionalNotes: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    setAvailableDates(generateFutureDates(7));
  }, []);

  const consultationTypes: ConsultationType[] = [
  {
    id: 'initial',
    type: 'Initial Consultation',
    duration: '30 minutes',
    description: 'Perfect for first-time explorers. Get personalized guidance on destination selection and program options.',
    price: 'Free',
    features: [
    'Destination recommendation based on goals',
    'Program and university overview',
    'Timeline and process explanation',
    'Q&A session with expert counselor'],

    isPopular: true
  },
  // {
  //   id: 'comprehensive',
  //   type: 'Comprehensive Planning',
  //   duration: '60 minutes',
  //   description: 'Deep-dive session for serious applicants. Detailed roadmap creation and application strategy.',
  //   price: '$99',
  //   features: [
  //   'Detailed university shortlisting',
  //   'Application timeline creation',
  //   'Document checklist preparation',
  //   'Scholarship opportunity identification',
  //   'Visa process walkthrough']

  // },
  {
    id: 'parent',
    type: 'Parent Consultation',
    duration: '45 minutes',
    description: 'Dedicated session for parents to address concerns about safety, finances, and student welfare.',
    price: 'Free',
    features: [
    'Safety and accommodation overview',
    'Financial planning and ROI discussion',
    'Communication and support systems',
    'Cultural adaptation guidance',
    'Emergency protocols explanation']

  }];


  const counselors: Counselor[] = [
  {
    id: '1',
    name: 'Mr. Ashok Variyani',
    title: 'Director',
    image: "/assets/images/My_Pic-1768281050610.png",
    alt: 'Professional Indian Man with glasses and black blazer smiling warmly in modern office',
    specializations: ['Dubai', 'Gurmany', 'France'],
    languages: ['English', 'Gujarati', 'Hindi'],
    experience: '12 years in international education',
    rating: 5,
    reviewCount: 169
  },
  {
    id: '2',
    name: 'Shreyanshi Gabhavala',
    title: 'Study Abroad Specialist',
    image: "/assets/images/Shreyanshi-1774854316518.jpeg",
    alt: 'Shreyanshi Gabhavala',
    specializations: ['Germany', 'France', 'Singapore', 'Dubai'],
    languages: ['English', 'Hindi', 'Gujarati'],
    experience: '5 years helping students globally',
    rating: 5,
    reviewCount: 189
    // },
    // {
    //   id: '3',
    //   name: 'Priya Sharma',
    //   title: 'International Admissions Expert',
    //   image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f953953d-1763294126115.png",
    //   alt: 'Indian woman with long dark hair in professional burgundy blazer with warm smile',
    //   specializations: ['China', 'Vietnam', 'Philippines'],
    //   languages: ['English', 'Hindi', 'Mandarin'],
    //   experience: '15 years in admissions consulting',
    //   rating: 5.0,
    //   reviewCount: 312
    // },
    // {
    //   id: '4',
    //   name: 'Ahmed Al-Mansouri',
    //   title: 'Middle East Education Specialist',
    //   image: "https://img.rocket.new/generatedImages/rocket_gen_img_19def871e-1763299283067.png",
    //   alt: 'Middle Eastern man in professional attire with warm smile',
    //   specializations: ['Saudi Arabia', 'Dubai', 'South Korea'],
    //   languages: ['English', 'Arabic', 'Korean'],
    //   experience: '10 years in Middle East education',
    //   rating: 4.9,
    //   reviewCount: 156
    // 
  }];



  const timeSlots: TimeSlot[] = [
  { id: '1', time: '10:00 AM', isAvailable: false },
  { id: '2', time: '11:00 AM', isAvailable: true },
  { id: '3', time: '12:00 PM', isAvailable: true },
  { id: '4', time: '01:00 PM', isAvailable: false },
  { id: '5', time: '02:00 PM', isAvailable: true },
  { id: '6', time: '03:00 PM', isAvailable: true },
  { id: '7', time: '04:00 PM', isAvailable: true },
  { id: '8', time: '05:00 PM', isAvailable: true }];

  const handleConsultationTypeSelect = (typeId: string) => {
    setSelectedConsultationType(typeId);
    setCurrentStep(2);
  };

  const handleCounselorSelect = (counselorId: string) => {
    setSelectedCounselor(counselorId);
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleChecklistToggle = (itemId: string) => {
    setChecklistItems((items) =>
    items.map((item) =>
    item.id === itemId ? { ...item, completed: !item.completed } : item
    )
    );
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    setShowSuccessModal(true);
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedConsultationType !== '';
      case 2:
        return selectedCounselor !== '';
      case 3:
        return selectedDate !== '' && selectedTime !== '';
      case 4:
        return formData.fullName && formData.email && formData.phone && formData.preferredDestination && formData.currentEducation;
      default:
        return false;
    }
  };

  const getSelectedConsultationType = () => {
    return consultationTypes.find((type) => type.id === selectedConsultationType);
  };

  const getSelectedCounselor = () => {
    return counselors.find((counselor) => counselor.id === selectedCounselor);
  };

  const getSelectedDateDisplay = () => {
    return availableDates.find((date) => date.date === selectedDate)?.display || '';
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto" />
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Book Your Consultation
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Take the first step towards your international education journey. Our expert counselors are here to guide you every step of the way.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4].map((step) =>
            <div key={step} className="flex items-center flex-1">
                <div className="flex items-center">
                  <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-smooth ${
                  currentStep >= step ?
                  'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground'}`
                  }>
                    {step}
                  </div>
                  <span
                  className={`ml-3 text-sm font-medium hidden md:block ${
                  currentStep >= step ? 'text-foreground' : 'text-muted-foreground'}`
                  }>

                    {step === 1 && 'Type'}
                    {step === 2 && 'Counselor'}
                    {step === 3 && 'Schedule'}
                    {step === 4 && 'Details'}
                  </span>
                </div>
                {step < 4 &&
              <div
                className={`flex-1 h-1 mx-4 rounded transition-all duration-smooth ${
                currentStep > step ? 'bg-primary' : 'bg-muted'}`
                } />
              }
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {currentStep === 1 &&
            <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Choose Your Consultation Type
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {consultationTypes.map((type) =>
                <ConsultationTypeCard
                  key={type.id}
                  {...type}
                  onSelect={() => handleConsultationTypeSelect(type.id)} />

                )}
                </div>
              </div>
            }

            {currentStep === 2 &&
            <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Select Your Counselor
                </h2>
                <div className="space-y-6">
                  {counselors.map((counselor) =>
                <CounselorCard
                  key={counselor.id}
                  {...counselor}
                  isSelected={selectedCounselor === counselor.id}
                  onSelect={() => handleCounselorSelect(counselor.id)} />

                )}
                </div>
                <div className="mt-8 flex items-center justify-between">
                  <button
                  onClick={handlePrevStep}
                  className="px-6 py-3 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all duration-smooth">

                    Back
                  </button>
                  <button
                  onClick={handleNextStep}
                  disabled={!canProceedToNextStep()}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-smooth disabled:opacity-50 disabled:cursor-not-allowed">

                    Continue
                  </button>
                </div>
              </div>
            }

            {currentStep === 3 &&
            <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Choose Date & Time
                </h2>
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Select Date</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {availableDates.map((date) =>
                  <button
                    key={date.id}
                    onClick={() => handleDateSelect(date.date)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-smooth ${
                    selectedDate === date.date ?
                    'bg-primary text-primary-foreground' :
                    'bg-muted text-foreground hover:bg-muted/80'}`
                    }>

                        {date.display}
                      </button>
                  )}
                  </div>
                </div>

                {selectedDate &&
              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-foreground mb-4">Select Time</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {timeSlots.map((slot) =>
                  <TimeSlotButton
                    key={slot.id}
                    time={slot.time}
                    isSelected={selectedTime === slot.time}
                    isAvailable={slot.isAvailable}
                    onSelect={() => handleTimeSelect(slot.time)} />

                  )}
                    </div>
                  </div>
              }

                {/* <VideoTestTool /> */}

                <div className="mt-8 flex items-center justify-between">
                  <button
                  onClick={handlePrevStep}
                  className="px-6 py-3 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all duration-smooth">

                    Back
                  </button>
                  <button
                  onClick={handleNextStep}
                  disabled={!canProceedToNextStep()}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-smooth disabled:opacity-50 disabled:cursor-not-allowed">

                    Continue
                  </button>
                </div>
              </div>
            }

            {currentStep === 4 &&
            <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Your Contact Information
                </h2>
                
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <ContactForm formData={formData} onChange={handleFormChange} />
                </div>

                {/* <PreparationChecklist items={checklistItems} onToggle={handleChecklistToggle} /> */}

                <div className="mt-8 flex items-center justify-between">
                  <button
                  onClick={handlePrevStep}
                  className="px-6 py-3 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all duration-smooth">

                    Back
                  </button>
                  <button
                  onClick={handleSubmit}
                  disabled={!canProceedToNextStep()}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-smooth disabled:opacity-50 disabled:cursor-not-allowed">

                    Confirm Booking
                  </button>
                </div>
              </div>
            }
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {selectedConsultationType && selectedCounselor && selectedDate && selectedTime &&
              <BookingSummary
                consultationType={getSelectedConsultationType()?.type || ''}
                counselorName={getSelectedCounselor()?.name || ''}
                date={getSelectedDateDisplay()}
                time={selectedTime}
                duration={getSelectedConsultationType()?.duration || ''}
                price={getSelectedConsultationType()?.price || ''} />

              }

              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-border rounded-xl p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">What to Expect</h3>
                    <ul className="space-y-2 text-sm text-foreground">
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span>Personalized guidance based on your goals</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span>Expert insights on destination selection</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span>Clear roadmap for your application journey</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span>Answers to all your questions and concerns</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="ShieldCheckIcon" size={24} className="text-trust flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">100% Secure</h3>
                    <p className="text-sm text-muted-foreground">
                      Your information is protected with enterprise-grade security. We never share your data with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSuccessModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" variant="solid" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Booking Confirmed!</h2>
            <p className="text-muted-foreground mb-6">
              Your consultation has been successfully scheduled. We've sent a confirmation email with all the details and a calendar invite.
            </p>
            <div className="bg-muted rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-semibold text-foreground">{getSelectedDateDisplay()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time:</span>
                  <span className="font-semibold text-foreground">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Counselor:</span>
                  <span className="font-semibold text-foreground">{getSelectedCounselor()?.name}</span>
                </div>
              </div>
            </div>
            <button
            onClick={() => setShowSuccessModal(false)}
            className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-smooth">

              Got It!
            </button>
          </div>
        </div>
      }
    </div>);

}